// frontend/src/pages/Partner.js
//
// What a partner sees about their own money.
//
// The single design decision worth stating: PENDING and APPROVED are shown as
// two separate figures and never added together. A partner who reads one total
// and is paid a smaller one assumes they have been short-changed — and they are
// not wrong to, because "€47.50 earned" and "€31.00 paid" with no explanation in
// between is exactly what being short-changed looks like. So the page says which
// money is owed now, which is still inside its hold window, and why the window
// exists, before anyone has to ask.

import React, { useEffect, useState } from 'react';
import { Link } from '../routing';
import { useTranslation } from 'react-i18next';
import { getIntlLocale } from '../i18n';
import toast from 'react-hot-toast';
import {
  Copy, Check, TrendingUp, Clock, Wallet, Users, ExternalLink, FileText,
} from 'lucide-react';
import api from '../utils/api';
import { errorText } from '../utils/errors';
import Navbar from '../components/Navbar';
import PageHero from '../components/PageHero';
import theme from '../theme';

const money = (v) => `€${(parseFloat(v) || 0).toFixed(2)}`;

export default function Partner() {
  const { t } = useTranslation();
  const dateLocale = getIntlLocale();

  const [data, setData] = useState(null);
  const [assets, setAssets] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notPartner, setNotPartner] = useState(false);
  const [copied, setCopied] = useState(null);
  const [accepting, setAccepting] = useState(false);

  const load = React.useCallback(() => {
    setLoading(true);
    api.get('/affiliates/me')
      .then((me) => {
        setData(me);
        // Fired together rather than chained: three independent reads, and a
        // dashboard that waits for each in turn feels broken on a phone.
        return Promise.all([
          api.get('/affiliates/me/assets').then(setAssets),
          api.get('/affiliates/me/commissions').then(setCommissions),
          api.get('/affiliates/me/payouts').then(setPayouts),
        ]);
      })
      .catch((err) => {
        // 404 is the ordinary answer for a customer who is not a partner, not a
        // failure — see the note on requirePartner in routes/affiliates.js.
        if (err?.status === 404) setNotPartner(true);
        else toast.error(errorText(err, t('partner.loadError')));
      })
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(load, [load]);

  const copy = async (value, which) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(which);
      setTimeout(() => setCopied(null), 1800);
    } catch {
      // Clipboard access is refused outright in some in-app browsers. The value
      // is on screen either way, so say what to do rather than failing silently.
      toast(t('partner.copyManually'));
    }
  };

  const acceptTerms = async () => {
    setAccepting(true);
    try {
      await api.post('/affiliates/me/accept-terms', { accept: true });
      toast.success(t('partner.termsAccepted'));
      load();
    } catch (err) {
      toast.error(errorText(err, t('partner.termsFailed')));
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return <Shell><div style={centered}>{t('partner.loading')}</div></Shell>;
  }

  if (notPartner) {
    return (
      <Shell>
        <div style={{ ...centered, flexDirection: 'column', gap: 20, textAlign: 'center' }}>
          <p style={{ color: theme.grey550, fontSize: 15, maxWidth: 460, lineHeight: 1.7 }}>
            {t('partner.notAPartner')}
          </p>
          <Link to="/partners" style={ctaStyle}>{t('partner.becomeOne')}</Link>
        </div>
      </Shell>
    );
  }

  const { affiliate, balance, funnel, hold_days: holdDays } = data;
  const needsTerms = !affiliate.terms_accepted_at;

  return (
    <Shell
      eyebrow={t('partner.eyebrow')}
      title={affiliate.name}
      subtitle={t('partner.subtitle')}
    >
      {/* The agreement gate. Shown as a banner rather than a modal because it
          blocks payment, not access — a partner should be able to look at what
          they have earned before deciding whether to sign for it. */}
      {needsTerms && (
        <div style={{
          background: theme.creamDeep, border: `1px solid ${theme.gold}`, borderRadius: 4,
          padding: '20px 24px', marginBottom: 32,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
            <FileText size={18} color={theme.gold} style={{ marginTop: 2, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 260 }}>
              <p style={{ margin: 0, color: theme.ink, fontSize: 14, lineHeight: 1.7 }}>
                {t('partner.termsBanner')}
              </p>
              <Link to="/partner-terms" target="_blank"
                style={{ color: theme.gold, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 8 }}>
                {t('partner.readTerms')} <ExternalLink size={12} />
              </Link>
            </div>
            <button onClick={acceptTerms} disabled={accepting} style={{ ...ctaStyle, whiteSpace: 'nowrap' }}>
              {accepting ? t('partner.accepting') : t('partner.acceptTerms')}
            </button>
          </div>
        </div>
      )}

      {/* ── The money ── */}
      <div style={grid}>
        <Stat
          icon={<Wallet size={16} />}
          label={t('partner.owedNow')}
          value={money(balance.approved)}
          note={t('partner.owedNote')}
          accent
        />
        <Stat
          icon={<Clock size={16} />}
          label={t('partner.onHold')}
          value={money(balance.pending)}
          note={t('partner.holdNote', { days: holdDays })}
        />
        <Stat
          icon={<Check size={16} />}
          label={t('partner.paidToDate')}
          value={money(balance.paid)}
        />
        <Stat
          icon={<TrendingUp size={16} />}
          label={t('partner.lifetime')}
          value={money(balance.lifetime)}
          note={t('partner.lifetimeNote', { count: balance.commission_count })}
        />
      </div>

      {/* ── The funnel ── */}
      <Section title={t('partner.funnelTitle')}>
        <div style={grid}>
          <Stat icon={<Users size={16} />} label={t('partner.visitors')} value={funnel.visitors} />
          <Stat label={t('partner.clicks')} value={funnel.clicks} />
          <Stat label={t('partner.signups')} value={funnel.signups} />
          <Stat label={t('partner.bookings')} value={balance.commission_count} />
        </div>
        <p style={note}>{t('partner.funnelNote')}</p>
      </Section>

      {/* ── The code and the link ── */}
      <Section title={t('partner.yourCodeTitle')}>
        <div style={{ display: 'grid', gap: 16 }}>
          <CopyRow
            label={t('partner.code')}
            value={affiliate.code}
            copied={copied === 'code'}
            onCopy={() => copy(affiliate.code, 'code')}
            copyLabel={t('partner.copy')}
            mono
          />
          {assets && (
            <CopyRow
              label={t('partner.link')}
              value={assets.link}
              copied={copied === 'link'}
              onCopy={() => copy(assets.link, 'link')}
              copyLabel={t('partner.copy')}
            />
          )}
        </div>
        <p style={note}>
          {t('partner.rateLine', {
            rate: affiliate.commission_type === 'percentage'
              ? `${parseFloat(affiliate.commission_value)}%`
              : money(affiliate.commission_value),
          })}
        </p>
      </Section>

      {/* ── Commissions ── */}
      <Section title={t('partner.commissionsTitle')}>
        <Table
          head={[t('partner.colDate'), t('partner.colBasis'), t('partner.colAmount'), t('partner.colStatus')]}
          empty={t('partner.noCommissions')}
          rows={commissions.map((c) => [
            new Date(c.rental_paid_at || c.created_at).toLocaleDateString(dateLocale),
            money(c.basis_amount),
            money(c.amount),
            <StatusPill key="s" status={c.status} t={t} />,
          ])}
        />
      </Section>

      {/* ── Payouts ── */}
      <Section title={t('partner.payoutsTitle')}>
        <Table
          head={[t('partner.colDate'), t('partner.colAmount'), t('partner.colReference')]}
          empty={t('partner.noPayouts')}
          rows={payouts.map((p) => [
            new Date(p.paid_at).toLocaleDateString(dateLocale),
            money(p.amount),
            p.reference || '—',
          ])}
        />
      </Section>
    </Shell>
  );
}

// ── Presentation ───────────────────────────────────────────────────────────

function Shell({ eyebrow, title, subtitle, children }) {
  return (
    <div style={{ fontFamily: theme.font.body, background: theme.cream, minHeight: '100vh' }}>
      <Navbar />
      {title && <PageHero eyebrow={eyebrow} title={title} subtitle={subtitle} height={260} />}
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '56px 24px 96px' }}>{children}</div>
    </div>
  );
}

const centered = { minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.grey400 };
const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 };
const note = { color: theme.grey400, fontSize: 12.5, lineHeight: 1.7, margin: '14px 0 0' };
const ctaStyle = {
  display: 'inline-block', background: theme.ink, color: theme.white, textDecoration: 'none',
  border: 'none', padding: '13px 24px', borderRadius: 2, fontSize: 12,
  letterSpacing: '0.12em', textTransform: 'uppercase', cursor: 'pointer',
  fontFamily: theme.font.body,
};

function Section({ title, children }) {
  return (
    <section style={{ marginTop: 48 }}>
      <h2 style={{
        fontFamily: theme.font.display, fontSize: 24, fontWeight: 400,
        color: theme.ink, margin: '0 0 20px',
      }}>{title}</h2>
      {children}
    </section>
  );
}

function Stat({ icon, label, value, note: hint, accent }) {
  return (
    <div style={{
      background: theme.white,
      border: `1px solid ${accent ? theme.gold : theme.creamBone}`,
      borderRadius: 4, padding: '20px 22px',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 7, color: theme.grey400,
        fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase',
      }}>
        {icon}{label}
      </div>
      <div style={{
        fontFamily: theme.font.display, fontSize: 30, color: theme.ink, marginTop: 6, lineHeight: 1.1,
      }}>{value}</div>
      {hint && <p style={{ ...note, marginTop: 8 }}>{hint}</p>}
    </div>
  );
}

function CopyRow({ label, value, onCopy, copied, copyLabel, mono }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: theme.grey400, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 7 }}>
        {label}
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'stretch', flexWrap: 'wrap' }}>
        <div style={{
          flex: 1, minWidth: 220, background: theme.white, border: `1px solid ${theme.creamBone}`,
          borderRadius: 4, padding: '13px 16px', fontSize: 14, color: theme.ink,
          fontFamily: mono ? theme.font.mono : theme.font.body,
          letterSpacing: mono ? '0.08em' : 0,
          overflowWrap: 'anywhere',
        }}>{value}</div>
        <button onClick={onCopy} style={{
          ...ctaStyle, display: 'inline-flex', alignItems: 'center', gap: 7,
          background: copied ? theme.green : theme.ink,
        }}>
          {copied ? <Check size={14} /> : <Copy size={14} />} {copyLabel}
        </button>
      </div>
    </div>
  );
}

function StatusPill({ status, t }) {
  const palette = {
    pending:   { bg: theme.creamDeep, fg: theme.grey550 },
    approved:  { bg: theme.greenWash, fg: theme.greenDeep },
    paid:      { bg: theme.greenWash, fg: theme.greenDeep },
    cancelled: { bg: theme.grey100,   fg: theme.grey450 },
  }[status] || { bg: theme.grey100, fg: theme.grey450 };

  return (
    <span style={{
      background: palette.bg, color: palette.fg, padding: '3px 10px',
      borderRadius: 100, fontSize: 11, letterSpacing: '0.05em', fontWeight: 500,
    }}>
      {t(`partner.status.${status}`)}
    </span>
  );
}

function Table({ head, rows, empty }) {
  return (
    <div style={{
      background: theme.white, border: `1px solid ${theme.creamBone}`,
      borderRadius: 4, overflowX: 'auto',
    }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 420 }}>
        <thead>
          <tr style={{ background: theme.cream }}>
            {head.map((h) => (
              <th key={h} style={{
                padding: '13px 16px', textAlign: 'left', color: theme.grey400,
                fontWeight: 500, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase',
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={head.length} style={{ padding: 40, textAlign: 'center', color: theme.grey400 }}>{empty}</td></tr>
          ) : rows.map((cells, i) => (
            <tr key={i} style={{ borderBottom: `1px solid ${theme.creamDeep}` }}>
              {cells.map((cell, j) => (
                <td key={j} style={{ padding: '15px 16px', color: theme.grey600 }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
